import React, { useCallback, useEffect, useState } from 'react';
import {
  GroupManagementListI,
  LogDisplayItem,
  LogEntry,
  Meta,
  OptionI,
  paramsI,
  UserFormInterface
} from '../../../../types/interface';
import { Card, Form } from 'react-bootstrap';
import {
  capitalizeFirstLetter,
  checkAccess,
  convertToLabelValue,
  isProd,
  logEntriesToDisplayItems,
  parseIntoNumber
} from 'utils/helpers';
import { t } from 'i18next';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { USER } from 'constant/RoutesUrls';
import StandardInput from 'base/StandardInput/StandardInput';
import { UserFormValidationSet } from 'utils/validations/ValidationsSet/UserFormValidationSet';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import { useAppDispatch } from '../../../redux/HooksRedux';
import * as userAction from '../../../redux/user/userAction';
import * as logAction from '../../../redux/logs/logAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { StandardRadio } from 'base/StandardRadio/StandardRadio';
import StandardCheckbox from 'base/StandardCheckbox/StandardCheckbox';
import StandardTooltip from 'base/StandardTooltip/StandardTooltip';
import { tabData, user_type } from 'utils/staticData';
import validationFunctions from 'utils/validationUtils';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import {
  AccountType,
  ACTIVE_EMPLOYEE,
  EARLY_RETIREMENT,
  ENGLISH,
  GERMAN,
  logPerPage,
  LogType,
  modules,
  SELECTED_LANGUAGE,
  ViewAllLimit
} from 'utils/enums/misc';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getCookie } from 'utils/cookieUtils';
import { StandardLoader } from 'base/Loader/Loader';
import { AxiosResponse } from 'axios';
import LogTimeline from 'components/timelines/LogTimeline';
import StandardTab from 'base/StandardTab/StandardTab';
import InfinityScroll from 'base/InfonityScroll/InfinityScroll';
import { getAdminLevels } from '../../../redux/AdminLevel/AdminLevelAction';
import { getAllGroupManegement } from '../../../redux/GroupManagement/GroupManagementAction';

const UserForm = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const accountTypeParam = searchParams.get('type') as 'admin' | 'user' | null;

  const [formData, setFormData] = useState<UserFormInterface>({
    email: '',
    password: '',
    role_ids: undefined,
    firstName: '',
    user_type: EARLY_RETIREMENT,
    lastName: '',
    group_ids: []
  });
  const [showError, setShowError] = useState<boolean>(false);
  const [roleList, setRoleList] = useState<OptionI[]>([]);
  const [adminLevelGroups, setAdminLevelGroups] = useState<OptionI[]>([]);
  const [userType, setUserType] = useState<AccountType>(
    accountTypeParam === 'user' ? AccountType.USER : AccountType.ADMIN
  );
  const [filteredOptions, setFilteredOptions] = useState<OptionI[]>([]);
  const [logs, setLogs] = useState<LogDisplayItem[]>([]);
  const [activeTab, setActiveTab] = useState<LogType | null>(LogType.FORM);
  const [option, setOption] = useState([
    {
      label: capitalizeFirstLetter(t('user.option.label_2')),
      value: EARLY_RETIREMENT
    },
    {
      label: capitalizeFirstLetter(
        t('register_applicant.radioButtons1.label_1')
      ),
      value: ACTIVE_EMPLOYEE
    }
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [logCurrentPage, setLogCurrentPage] = useState(1);
  const [logLoading, setLogLoading] = useState(false);
  const [logTotalPage, setLogTotalPage] = useState(1);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);

  const dispatch = useAppDispatch();
  const lang = getCookie(SELECTED_LANGUAGE) || GERMAN;
  const { permission, userProfile } = useSelector(
    (state: RootState) => state.userProfile
  );
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const handleSubmit = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    const validations = { ...UserFormValidationSet };
    const isAdminType =
      userType === AccountType.ADMIN || userType === AccountType.SUPER_ADMIN;
    const apiData = {
      ...formData,
      user_type: isAdminType ? '' : formData.user_type,
      account_type: userType.toUpperCase()
    };
    if (userId) {
      delete validations.password;
    }
    if (isAdminType) {
      delete validations.user_type;
    }
    if (userType === AccountType.USER) {
      delete validations.group_ids;
    }

    const isValidForm: boolean = validationFunctions.checkFormValidity(
      formData!,
      validations
    );

    if (!isValidForm) {
      setShowError(true);
      setIsLoading(false);
      return;
    }

    setShowError(false);

    if (
      userId &&
      checkAccess([modules.USERS.edit], permission) &&
      userProfile.account_type === AccountType.SUPER_ADMIN
    ) {
      const id = parseInt(userId);

      try {
        const response = await dispatch(userAction.updateUser(id, apiData));

        if (response?.status === 200) {
          successNotification(response.data?.message);
          navigate(USER);
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    } else if (
      checkAccess([modules.USERS.create], permission) &&
      userProfile.account_type === AccountType.SUPER_ADMIN
    ) {
      try {
        const response = await dispatch(userAction.createUser(apiData));
        if (response?.status === 201) {
          successNotification(response.data?.message);
          navigate(USER);
          setIsLoading(false);
        } else {
          errorNotification(response?.data?.message);
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error);
        setIsLoading(false);
      }
    } else {
      navigate(USER);
      setIsLoading(false);
    }
  };

  const getSingleUser = async () => {
    setIsLoading(true);
    if (
      !checkAccess([modules.USERS.edit], permission) &&
      userProfile.account_type === AccountType.SUPER_ADMIN
    ) {
      navigate(USER);
      setIsLoading(false);
      return;
    }
    try {
      const response = await dispatch(
        userAction.getSingleUser(parseFloat(userId!))
      );
      if (response.status === 200) {
        console.log(response.data?.data);

        setFormData({
          email: response.data?.data?.email,
          firstName: response.data?.data?.first_name,
          is_archived: response.data?.data?.is_archived,
          lastName: response.data?.data?.last_name,
          password: response.data?.data?.password,
          role_ids:
            response.data?.data?.roles.map(
              (r: { role_id: number }) => r.role_id
            ) ?? undefined,
          user_type: response.data?.data?.user_type,
          group_ids: response.data?.data?.groups.map(
            (g: { group_id: number }) => g.group_id
          )
        });
        setUserType(response.data?.data?.account_type);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  console.log(formData, userId);

  const getUserLog = useCallback(
    async (userID: number) => {
      try {
        const params: paramsI = {
          page: 1,
          limit: logPerPage * logCurrentPage,
          keyword: ''
        };
        const response: AxiosResponse<{ logs: LogEntry[]; meta: Meta }> =
          await dispatch(logAction.getLogByUserId(userID, params));
        setLogTotalPage(response.data.meta.totalPages);
        if (params.limit >= response.data.meta.totalRecords) {
          setHasMoreLogs(false);
        } else {
          setHasMoreLogs(true);
        }

        setLogs(logEntriesToDisplayItems(response.data.logs, lang === GERMAN));
      } catch (error) {
        console.error(error);
      } finally {
        setLogLoading(false);
      }
    },
    [logCurrentPage, logPerPage]
  );

  const handleCancle = () => {
    navigate(USER);
  };

  const handleChange = (
    value: UserFormInterface[keyof UserFormInterface],
    name: keyof UserFormInterface
  ) => {
    setFormData({
      ...formData!,
      [name]: value
    });
  };

  const getAllRole = async () => {
    try {
      const response = await dispatch(userAction.getAllRole());

      if (response.status === 200) {
        const roleNameKey = lang === GERMAN ? 'role_name_de' : 'role_name';
        const convertValue = convertToLabelValue(
          response.data?.data?.roles?.filter(
            (role: { is_admin: boolean }) => role?.is_admin === false
          ),
          roleNameKey,
          'role_id'
        );
        setRoleList(convertValue);
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllRole();
    // if (!isProd) {
    getAllAdminGroup();
    // }
  }, []);

  useEffect(() => {
    if (userId) {
      getSingleUser();
    }
  }, [userId]);

  useEffect(() => {
    if (
      userType === AccountType.ADMIN ||
      userType === AccountType.SUPER_ADMIN
    ) {
      const adminOptions = roleList?.filter(
        item => item.label?.toLowerCase() !== 'user'
      );
      setFilteredOptions(adminOptions);
      const validRoleIds =
        (formData.role_ids as number[])?.filter(
          id => adminOptions?.some(item => item.value === id)
        ) ?? [];
      setFormData(prev => ({ ...prev, role_ids: validRoleIds }));
    } else {
      const isThereRoleUser = roleList?.some(
        item => item.label?.toLowerCase() === 'user'
      );
      if (isThereRoleUser) {
        setFilteredOptions(
          roleList?.filter(item => item.label?.toLowerCase() === 'user')
        );
      } else {
        setFilteredOptions([
          {
            label: capitalizeFirstLetter(t('header.text.text_2')),
            value: 2
          }
        ]);
      }

      setFormData({ ...formData, role_ids: [2] });
    }
    if (!userId) {
      setOption(prev => {
        return prev.filter(item => item.value === EARLY_RETIREMENT);
      });
    }
  }, [userType, roleList]);

  const getAllAdminGroup = async () => {
    const params: paramsI = {
      page: 1,
      limit: ViewAllLimit,
      keyword: ''
    };
    let languageString: keyof GroupManagementListI =
      currentLanguage === ENGLISH ? 'group_name_en' : 'group_name_de';
    try {
      const response: AxiosResponse<{ data: GroupManagementListI[] }> =
        await dispatch(getAllGroupManegement(params));
      const formatedData: OptionI[] = response.data?.data.map(g => {
        return {
          label: g[languageString] as string,
          value: parseIntoNumber(g.group_id)
        };
      });
      setAdminLevelGroups(formatedData);
    } catch (error) {
      console.log('Error while getting state', error);
    }
  };

  useEffect(() => {
    if (userId && activeTab === LogType.LOGS) {
      getUserLog(Number(userId));
    }
  }, [userId, logCurrentPage]);

  useEffect(() => {
    if (activeTab === LogType.LOGS && logs.length === 0) {
      getUserLog(Number(userId));
    }
  }, [activeTab]);

  return (
    <Card>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <Form onSubmit={handleSubmit}>
          <Card.Header className="py-2">
            <div className="row justify-content-between align-items-center">
              <div className="w-auto">
                <h3 className="mb-0">
                  {userType === AccountType.USER
                    ? capitalizeFirstLetter(t('user.account_type.user'))
                    : capitalizeFirstLetter(t('user.account_type.admin'))}
                </h3>
              </div>
              <div className="d-flex gap-2 w-auto">
                <StandardButton
                  buttonText={capitalizeFirstLetter(
                    t('general.common_text.text_15')
                  )}
                  onClick={handleCancle}
                  variant="outline-danger"
                />

                <StandardButton
                  buttonText={
                    userId
                      ? capitalizeFirstLetter(t('general.common_text.text_18'))
                      : capitalizeFirstLetter(t('general.common_text.text_2'))
                  }
                  onClick={() => console.log('save')}
                  type="submit"
                />
              </div>
            </div>
          </Card.Header>

          <Card.Body>
            <ConditionalRender condition={!!userId}>
              <div>
                <StandardTab
                  activeKey={activeTab}
                  data={
                    userProfile.account_type === AccountType.SUPER_ADMIN
                      ? tabData
                      : tabData.filter(tab => tab.activeId !== LogType.LOGS)
                  }
                  onChange={val => setActiveTab(val)}
                />
              </div>
            </ConditionalRender>
            <ConditionalRender
              condition={activeTab === LogType.FORM}
              fallBack={
                <InfinityScroll
                  childrenIdPrefix="user-log"
                  currentPage={logCurrentPage}
                  setCurrentPage={setLogCurrentPage}
                  dataLength={logs.length}
                  hasMore={hasMoreLogs}
                  limitPerPage={logPerPage}
                  loading={logLoading}
                  setLoading={setLogLoading}
                >
                  <LogTimeline data={logs} idPrefix="user-log" />
                </InfinityScroll>
              }
            >
              <div>
                <div className="mb-3">
                  <p className="mb-2">{t('user.user_type.title')}</p>
                  <div className="d-flex gap-3">
                    <div className="d-flex align-items-start gap-1">
                      <StandardCheckbox
                        id="account-type-admin"
                        label={t('user.user_type.label_1')}
                        isChecked={userType === AccountType.ADMIN}
                        setChecked={checked => {
                          if (checked) setUserType(AccountType.ADMIN);
                        }}
                        disabled={
                          !!userId && userType === AccountType.SUPER_ADMIN
                        }
                      />
                      <StandardTooltip
                        text={t('user.user_type.admin_tooltip')}
                        placement="top"
                        icon
                        className="mt-1"
                      />
                    </div>
                    <StandardCheckbox
                      id="account-type-user"
                      label={t('user.account_type.user')}
                      isChecked={userType === AccountType.USER}
                      setChecked={checked => {
                        if (checked) setUserType(AccountType.USER);
                      }}
                      disabled={
                        !!userId && userType === AccountType.SUPER_ADMIN
                      }
                    />
                    <StandardCheckbox
                      id="account-type-super-admin"
                      label={t('user.account_type.super_admin')}
                      isChecked={userType === AccountType.SUPER_ADMIN}
                      setChecked={checked => {
                        if (checked) setUserType(AccountType.SUPER_ADMIN);
                      }}
                      disabled
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-md-6 col-lg-4">
                    <StandardInput
                      className=""
                      id=""
                      label={t('general.common_text.text_5')}
                      type="text"
                      value={formData!.firstName}
                      placeholder={t('general.common_text.text_5')}
                      onChange={e => {
                        handleChange(e as string, 'firstName');
                      }}
                      showError={showError}
                      validations={UserFormValidationSet.firstName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <StandardInput
                      className=""
                      id=""
                      label={t('general.common_text.text_6')}
                      type="text"
                      value={formData!.lastName}
                      placeholder={t('general.common_text.text_6')}
                      onChange={e => {
                        handleChange(e as string, 'lastName');
                      }}
                      showError={showError}
                      validations={UserFormValidationSet.lastName}
                    />
                  </div>
                  <div className="col-12 col-md-6 col-lg-4">
                    <StandardInput
                      className=""
                      id=""
                      label={t('general.common_text.text_4')}
                      type="email"
                      value={formData!.email.toLowerCase().trim()}
                      placeholder={t('general.common_text.text_4')}
                      onChange={e => {
                        handleChange(e as string, 'email');
                      }}
                      showError={showError}
                      validations={UserFormValidationSet.email}
                      disabled={!!userId}
                    />
                  </div>
                </div>
                <div className="row">
                  <ConditionalRender condition={!userId}>
                    <div className="col-12 col-md-6 col-lg-4">
                      <StandardInput
                        className=""
                        id=""
                        label={t('general.common_text.text_3')}
                        type="password"
                        value={formData!.password as string}
                        placeholder={t('general.common_text.text_3')}
                        onChange={e => {
                          handleChange(e as string, 'password');
                        }}
                        showError={showError}
                        validations={UserFormValidationSet.password || []}
                      />
                    </div>
                  </ConditionalRender>
                  <div className="col-12 col-md-6 col-lg-4">
                    <StandardSelect
                      formClassName=""
                      id=""
                      label={t('user.placeholder.text_6')}
                      onChange={e => {
                        handleChange(e, 'role_ids');
                      }}
                      options={filteredOptions}
                      showError={showError}
                      placeholder={t('user.placeholder.text_6')}
                      validations={UserFormValidationSet.role_ids}
                      value={formData!.role_ids}
                      disabled={userType === AccountType.USER}
                      isMulti
                    />
                  </div>
                  {/* <ConditionalRender condition={userType !== 'user' && !isProd}> */}
                  <div className="col-12 col-md-6 col-lg-4">
                    <StandardSelect
                      formClassName=""
                      id=""
                      label={t('user.placeholder.text_8')}
                      onChange={e => {
                        handleChange(e, 'group_ids');
                      }}
                      options={adminLevelGroups}
                      showError={showError}
                      placeholder={t('user.placeholder.text_8')}
                      validations={UserFormValidationSet.group_ids || []}
                      value={formData.group_ids}
                      disabled={userType === AccountType.USER}
                      isMulti
                    />
                  </div>
                  {/* </ConditionalRender> */}
                  <ConditionalRender condition={userType === AccountType.USER}>
                    <div className="col-4 d-flex align-items-center mt-2">
                      <StandardRadio
                        value={
                          formData.user_type === EARLY_RETIREMENT
                            ? EARLY_RETIREMENT
                            : ACTIVE_EMPLOYEE
                        }
                        onChange={e => {
                          handleChange(e as string, 'user_type');
                        }}
                        className={''}
                        showError={showError}
                        validations={UserFormValidationSet.user_type}
                        options={option}
                        disabled
                      />
                    </div>
                  </ConditionalRender>
                </div>
              </div>
            </ConditionalRender>
          </Card.Body>
        </Form>
      </ConditionalRender>
    </Card>
  );
};

export default UserForm;
